'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Children, useState, useRef, useMemo, isValidElement, cloneElement, useEffect } from 'react';
import c from 'react-slick';
import t from 'tiny-warning';
import { removeCoreProps } from '../core/core.utils.js';
import { useSlidesToShow } from '../adaptive-carousel/adaptive-carousel.hooks.js';
import { noTooltipInternal } from '../icon-button/icon-button-internal.utils.js';
import { useInternalOptionalSwanI18nContext } from '../../contexts/i18n/swan-i18n-provider.context.js';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { useDeprecatedProps } from '../../hooks/use-deprecated-props.js';
import { usePreventVerticalScroll } from '../../hooks/use-prevent-vertical-scroll.js';
import { useRequiredI18nProps, getDeprecatedI18nPropMessage } from '../../hooks/use-required-i18n-props.js';
import { usePromoBarDarkMode } from './carousel.hooks.js';
import { processStyleProps } from '../../../core/utilities/swan-style.js';
import { deprecatedComponent } from '../core/deprecated-component.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { useId } from '../../hooks/use-id.js';
import { assignRefs } from '../../../core/utilities/ref.utils.js';
import { className } from '../../../core/utilities/classnames.utils.js';
import { tokensRaw } from '../../../core/tokens/tokens-raw.js';
import { IconButton } from '../icon-button/icon-button.component.js';
import { VisuallyHidden } from '../visually-hidden/visually-hidden.component.js';
import { Icon } from '../icon/icon.component.js';

function _define_property(e, r, t) {
    return r in e ? Object.defineProperty(e, r, {
        value: t,
        enumerable: true,
        configurable: true,
        writable: true
    }) : e[r] = t, e;
}
function _object_spread(e) {
    for(var r = 1; r < arguments.length; r++){
        var t = null != arguments[r] ? arguments[r] : {}, o = Object.keys(t);
        "function" == typeof Object.getOwnPropertySymbols && (o = o.concat(Object.getOwnPropertySymbols(t).filter(function(e) {
            return Object.getOwnPropertyDescriptor(t, e).enumerable;
        }))), o.forEach(function(r) {
            _define_property(e, r, t[r]);
        });
    }
    return e;
}
function ownKeys(e, r) {
    var t = Object.keys(e);
    if (Object.getOwnPropertySymbols) {
        var o = Object.getOwnPropertySymbols(e);
        t.push.apply(t, o);
    }
    return t;
}
function _object_spread_props(e, r) {
    return r = null != r ? r : {}, Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(r)) : ownKeys(Object(r)).forEach(function(t) {
        Object.defineProperty(e, t, Object.getOwnPropertyDescriptor(r, t));
    }), e;
}
function _object_without_properties(e, r) {
    if (null == e) return {};
    var t, o, s = _object_without_properties_loose(e, r);
    if (Object.getOwnPropertySymbols) {
        var a = Object.getOwnPropertySymbols(e);
        for(o = 0; o < a.length; o++)t = a[o], !(r.indexOf(t) >= 0) && Object.prototype.propertyIsEnumerable.call(e, t) && (s[t] = e[t]);
    }
    return s;
}
function _object_without_properties_loose(e, r) {
    if (null == e) return {};
    var t, o, s = {}, a = Object.keys(e);
    for(o = 0; o < a.length; o++)t = a[o], r.indexOf(t) >= 0 || (s[t] = e[t]);
    return s;
}
let CarouselButton = ({ onClick: t, accessibleText: o, iconType: s, className: a, style: i })=>{
    let l = useId();
    return jsxs(IconButton, _object_spread_props(_object_spread({
        tooltip: ""
    }, noTooltipInternal), {
        onClick: t,
        disabled: null == a ? void 0 : a.includes('slick-disabled'),
        className: a,
        style: i,
        "aria-labelledby": l,
        children: [
            jsx(VisuallyHidden, {
                id: l,
                children: o
            }),
            jsx(Icon, {
                alt: "",
                iconType: s
            })
        ]
    }));
};
const UninitializedCarousel = (r)=>{
    let { children: o, className: s } = r;
    return jsx("div", {
        className: `swan-carousel swan-carousel-placeholder ${s}`,
        children: jsx("div", {
            className: "slick-list",
            children: jsx("div", {
                className: "slick-track",
                children: Children.map(o, (r)=>jsx("div", {
                        className: "slick-slide",
                        children: jsx("div", {
                            children: r
                        })
                    }))
            })
        })
    });
};
let A = renderWithRef('Carousel', null, (w, y)=>{
    useComponentStylesLoaded('Carousel', SWAN_STYLE_KEY_MAP.carousel);
    let { as: g, component: _, skin: j = 'standard', progressIndicator: x = 'none', progressIndicatorAlignment: A = false, peek: D = false, gridGutters: V = false, gridGuttersVariant: B = 'standard', arrowVerticalPosition: I, arrowVerticalOffset: E = '0px', children: G, dots: M = false, arrows: K = true, variableWidth: U = false, centerMode: $ = false, slidesToShow: z = 1, centerPadding: R, role: W = 'region', 'aria-label': q, 'aria-labelledby': L, 'aria-roledescription': X, accessibleTextForPrevious: H, accessibleTextForNext: J, accessibleTextForDots: Q, accessibleTextForVisibleSlides: Y, accessibleTextForSlide: Z, customPaging: ee, currentSlide: er, initialSlide: et = er, id: eo, fade: es, darkMode: ea } = w, ei = _object_without_properties(w, [
        "as",
        "component",
        "skin",
        "progressIndicator",
        "progressIndicatorAlignment",
        "peek",
        "gridGutters",
        "gridGuttersVariant",
        "arrowVerticalPosition",
        "arrowVerticalOffset",
        "children",
        "dots",
        "arrows",
        "variableWidth",
        "centerMode",
        "slidesToShow",
        "centerPadding",
        "role",
        'aria-label',
        'aria-labelledby',
        'aria-roledescription',
        "accessibleTextForPrevious",
        "accessibleTextForNext",
        "accessibleTextForDots",
        "accessibleTextForVisibleSlides",
        "accessibleTextForSlide",
        "customPaging",
        "currentSlide",
        "initialSlide",
        "id",
        "fade",
        "darkMode"
    ]), el = useInternalOptionalSwanI18nContext();
    useRequiredI18nProps(w, [
        'accessibleTextForPrevious',
        'accessibleTextForNext'
    ]);
    let en = H, ec = J, ed = Q, ep = X, eu = Children.toArray(G).length;
    el && (H || (en = el.t('carousel.accessible-text-for-previous-button')), J || (ec = el.t('carousel.accessible-text-for-next-button')), Q || (ed = Array.from({
        length: eu
    }, (e, r)=>el.t('carousel.accessible-text-for-dot-button', {
            slideNumber: r + 1
        }))), X || (ep = el.t('carousel.aria-roledescription'))), useDeprecatedProps('Carousel', w, [
        {
            propName: 'gridGuttersVariant',
            instructions: 'It is rarely used, and has been deprecated to improve consistency.'
        },
        ...[
            'accessibleTextForPrevious',
            'accessibleTextForNext',
            'accessibleTextForDots',
            'accessibleTextForVisibleSlides',
            'accessibleTextForSlide',
            'aria-roledescription'
        ].map((e)=>({
                propName: e,
                instructions: getDeprecatedI18nPropMessage(el)
            }))
    ]);
    let [eb, em] = useState('promo-bar' === j), ef = useId(eo), eh = `swan-carousel-placeholder-${ef}`, ew = useRef(null), ey = useRef(void 0), ev = assignRefs(y, ey), eg = M || 'none' !== x || !!ee, { forceDarkMode: e_, beforeChange: ej } = usePromoBarDarkMode(ey, w), ex = useSlidesToShow(z), eO = {
        prevArrow: jsx(CarouselButton, {
            accessibleText: en,
            iconType: "chevronLeft"
        }),
        nextArrow: jsx(CarouselButton, {
            accessibleText: ec,
            iconType: "chevronRight"
        }),
        arrows: K,
        centerMode: !!($ || D),
        dots: eg,
        customPaging: eg ? ee || ((r)=>jsx("button", {
                children: Array.isArray(ed) && void 0 != ed[r] ? ed[r] : r + 1
            })) : void 0,
        appendDots: eg ? (r)=>jsx("ul", {
                children: Children.map(r, (e)=>isValidElement(e) ? cloneElement(e, {}, cloneElement(e.props.children, {
                        'aria-current': 'slick-active' === e.props.className
                    })) : e)
            }) : void 0,
        initialSlide: 'number' == typeof et ? et : D && ex > 1 ? 1 : 0,
        slidesToShow: ex,
        variableWidth: U,
        autoplay: false,
        fade: null != es ? es : 'promo-bar' === j
    }, eS = {
        arrows: K
    };
    void 0 !== R && (eO.centerPadding = R, eS.centerPadding = R), D && (eS.centerPadding = '8'), t(eg || K, 'At least one of dots or arrow must be enabled for a carousel'), t(void 0 === g && void 0 === _, 'Carousel does not support the `component` or `as` prop because it must render a third-party component');
    let ek = processStyleProps(_object_spread_props(_object_spread({}, ei), {
        darkMode: e_ || ea
    })), eP = removeCoreProps(ei), eT = className(ei.className, ...ek, 'swan-carousel', {
        [`swan-carousel-skin-${j}`]: 'standard' !== j,
        'swan-carousel-dots': eO.dots,
        'swan-carousel-dots-left': eO.dots && 'left' === A,
        'swan-carousel-dots-inset': 'dots-inset' === x,
        'swan-carousel-hide-arrows': !eO.arrows,
        'swan-carousel-variable-width': eO.variableWidth,
        'swan-carousel-grid-gutters': V,
        'swan-carousel-grid-gutters-tight': V && 'tight' === B,
        ['swan-carousel-peek-non-focusable-' + ex]: !!D && ex % 2 == 0,
        [eh]: !eb
    }), eN = {
        '--swan-internal-carousel-placeholder-slides-to-show-derived': ex || 1,
        '--swan-internal-carousel-placeholder-slides-to-show-xs': 'object' == typeof z ? z.xs : void 0,
        '--swan-internal-carousel-placeholder-slides-to-show-sm': 'object' == typeof z ? z.sm : void 0,
        '--swan-internal-carousel-placeholder-slides-to-show-md': 'object' == typeof z ? z.md : void 0,
        '--swan-internal-carousel-placeholder-slides-to-show-lg': 'object' == typeof z ? z.lg : void 0,
        '--swan-internal-carousel-placeholder-slides-to-show-xl': 'object' == typeof z ? z.xl : void 0
    };
    usePreventVerticalScroll(ew);
    let [eC, eF] = useState(null), eA = useMemo(()=>{
        let e = [];
        if (null === eC) return null;
        let r = eC + 1;
        for(let t = 0; t < ex; t++)e.push(r), r === eu ? r = 1 : r++;
        let t = e[0].toString(), o = e[e.length - 1].toString();
        return el && !Y ? el.t(el.getPluralKey('carousel.accessible-text-for-visible-slides', ex), {
            start: t,
            end: o,
            totalSlides: eu
        }) : (null != Y ? Y : 1 === ex ? 'Slide {start} of {totalSlides}' : 'Slides {start} through {end} of {totalSlides}').replace('{start}', t).replace('{end}', o).replace('{totalSlides}', eu.toString()).replace('{activeSlides}', e.join(', '));
    }, [
        eC,
        Y,
        el,
        ex,
        eu
    ]), eD = useMemo(()=>Children.map(G, (e, r)=>{
            if (isValidElement(e) && void 0 === e.props['aria-label']) {
                let t = (r + 1).toString();
                return el && !Z ? cloneElement(e, {
                    'aria-label': el.t('carousel.accessible-text-for-slide', {
                        current: t,
                        totalSlides: eu
                    })
                }) : cloneElement(e, {
                    'aria-label': (null != Z ? Z : '{current} of {totalSlides}').replace('{current}', t).replace('{totalSlides}', eu.toString())
                });
            }
            return e;
        }), [
        Z,
        G,
        el,
        eu
    ]);
    return useEffect(()=>{
        em(true);
    }, []), useEffect(()=>{
        'number' == typeof er && ey.current && ey.current.slickGoTo(er);
    }, [
        er
    ]), (ex > eu || 1 === eu && 1 === ex) && (eO.infinite = false), jsxs("div", {
        className: "swan-carousel-container",
        ref: ew,
        "aria-label": q,
        "aria-labelledby": L,
        "aria-roledescription": ep || 'carousel',
        role: W,
        style: eN,
        children: [
            eb ? jsx(c, _object_spread_props(_object_spread(_object_spread_props(_object_spread({
                responsive: [
                    {
                        breakpoint: parseInt(tokensRaw.SwanBaseBreakpointXsEnd, 10),
                        settings: eS
                    }
                ]
            }, eP, eO), {
                ref: ev,
                className: eT
            }), {
                children: eD,
                style: {
                    '--swan-internal-carousel-arrow-top': I,
                    '--swan-internal-carousel-arrow-vertical-offset': E
                }
            }), {
                beforeChange: (e, r)=>{
                    ej(e, r), eF(r);
                }
            })) : jsx(UninitializedCarousel, {
                className: eT,
                children: G
            }),
            jsx("div", {
                "data-testid": "carousel-aria-live",
                className: "swan-carousel-current-visible-slides",
                "aria-live": "polite",
                children: eb ? eA : null
            })
        ]
    });
});
const Carousel = deprecatedComponent({
    description: 'Use AdaptiveCarousel instead',
    displayName: 'Carousel',
    root: A
});

export { Carousel, UninitializedCarousel };
